import React from "react";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    Tooltip as ReTooltip,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";

function AIPricingAndForecast({ selectedCompany, companies, selectedTab, accentColor }) {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [data, setData] = React.useState(null);

    const company = React.useMemo(
        () => companies?.find((c) => String(c.id) === String(selectedCompany)),
        [companies, selectedCompany]
    );

    React.useEffect(() => {
        if (!selectedCompany) {
            setData(null);
            setError(null);
            return;
        }

        const ctrl = new AbortController();
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            // 👉 Update this to your real AI endpoint when ready.
            // Expected response shape below in mock.
            const endpoint = `/api/ai/suggest?companyId=${encodeURIComponent(
                selectedCompany
            )}&mode=${encodeURIComponent(selectedTab || "production")}`;

            try {
                let res;
                try {
                    res = await fetch(endpoint, { signal: ctrl.signal });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const json = await res.json();
                    setData(json);
                } catch (e) {
                    // Fallback mock so the UI works before your backend is ready.
                    const mock = makeMockSuggestion(company?.name, selectedTab);
                    setData(mock);
                }
            } catch (e) {
                if (e.name !== "AbortError") setError(e.message || "Failed to load AI suggestion");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        return () => ctrl.abort();
    }, [selectedCompany, selectedTab, company?.name]);

    const label = selectedTab === "consumption" ? "Consumption" : "Production";

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-500">
                    AI Suggested Price & Forecast
                    {company?.name ? <span className="text-gray-400"> • {company.name}</span> : null}
                </h3>
                <div className="text-xs text-gray-400">
                    Mode: <span className="font-medium">{label}</span>
                </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4 bg-white">
                {/* Loading */}
                {loading && (
                    <div className="animate-pulse">
                        <div className="h-4 w-40 bg-gray-200 rounded mb-4" />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="border border-gray-100 rounded-lg p-3">
                                    <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
                                    <div className="h-5 w-16 bg-gray-200 rounded" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Error */}
                {!loading && error && (
                    <div className="text-sm text-red-600">
                        Couldn’t load AI suggestion: {error}
                    </div>
                )}

                {/* Content */}
                {!loading && !error && data && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <StatCard
                                title="Suggested Buy Price"
                                value={formatCurrency(data.buyPrice)}
                                subtitle={data.currency || "USD"}
                                accent={accentColor}
                            />
                            <StatCard
                                title={`Next-Month ${label} Est.`}
                                value={formatNumber(data.nextMonthProductionMWh)}
                                subtitle="MWh"
                                accent={accentColor}
                            />
                            <StatCard
                                title="Price Range (30d)"
                                value={
                                    formatCurrency(data.priceLow) + " – " + formatCurrency(data.priceHigh)
                                }
                                subtitle={data.currency || "USD"}
                                accent={accentColor}
                            />
                            <StatCard
                                title="Model Confidence"
                                value={`${Math.round(data.confidence * 100)}%`}
                                subtitle="(higher is better)"
                                accent={accentColor}
                            />

                            <StatCard
                                title="Suggested Buy Window"
                                value={formatDateRange(data.buyWindowStart, data.buyWindowEnd)}
                                subtitle="(local time)"
                                accent={accentColor}
                            />
                            <StatCard
                                title="Risk Score"
                                value={`${data.riskScore}/10`}
                                subtitle={data.riskNote || "lower is better"}
                                accent={accentColor}
                            />
                            <StatCard
                                title="Expected ROI (30d)"
                                value={`${toSignedPct(data.expectedRoiPct)}`}
                                subtitle="estimate"
                                accent={accentColor}
                            />
                            <StatCard
                                title="Signal Drivers"
                                value={data.topDrivers?.slice(0, 2).join(" • ") || "—"}
                                subtitle="key factors"
                                accent={accentColor}
                            />
                        </div>

                        {/* Tiny sparkline / expected price path */}
                        {Array.isArray(data.pricePath) && data.pricePath.length > 1 && (
                            <div className="mt-6">
                                <div className="text-xs text-gray-400 mb-2">
                                    Model price path (next 30 days)
                                </div>
                                <div className="h-36">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={data.pricePath} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                                            <defs>
                                                <linearGradient id="aiArea" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={accentColor} stopOpacity={0.25} />
                                                    <stop offset="100%" stopColor={accentColor} stopOpacity={0.02} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                                            <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                                            <ReTooltip
                                                contentStyle={{
                                                    background: "white",
                                                    border: "none",
                                                    borderRadius: "8px",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                                }}
                                                formatter={(val) => [formatCurrency(val), "Price"]}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="price"
                                                stroke={accentColor}
                                                strokeWidth={2}
                                                fill="url(#aiArea)"
                                                activeDot={{ r: 4 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {data.notes && (
                            <div className="mt-4 text-xs text-gray-500 leading-5">
                                <span className="font-medium text-gray-600">Model notes: </span>
                                {data.notes}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/* ---------- Small stat card ---------- */
function StatCard({ title, value, subtitle, accent }) {
    return (
        <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500">{title}</div>
            <div className="mt-1 text-base font-semibold" style={{ color: "#111827" }}>
                {value ?? "—"}
            </div>
            {subtitle && <div className="text-xs text-gray-400">{subtitle}</div>}
            <div className="mt-2 h-1.5 rounded-full" style={{ background: accent, opacity: 0.15 }} />
        </div>
    );
}

/* ---------- Utilities + Mock ---------- */
function formatCurrency(n, currency = "USD") {
    if (n == null || isNaN(n)) return "—";
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
    } catch {
        return `$${Number(n).toLocaleString()}`;
    }
}
function formatNumber(n) {
    if (n == null || isNaN(n)) return "—";
    return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function toSignedPct(p) {
    if (p == null || isNaN(p)) return "—";
    const v = Number(p);
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(2)}%`;
}
function formatDateRange(startIso, endIso) {
    if (!startIso || !endIso) return "—";
    const s = new Date(startIso);
    const e = new Date(endIso);
    const opts = { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
    return `${s.toLocaleString(undefined, opts)} – ${e.toLocaleString(undefined, opts)}`;
}

function makeMockSuggestion(companyName = "Company", mode = "production") {
    // Deterministic-ish seed from name+mode for stable mocks in UI
    const seed = [...(`${companyName}-${mode}`)].reduce((a, c) => a + c.charCodeAt(0), 0);
    const rand = (min, max, i = 1) => {
        const r = Math.abs(Math.sin(seed * (i + 1))) % 1;
        return min + r * (max - min);
    };

    const basePrice = rand(40, 140, 1);
    const vol = rand(0.8, 1.2, 2);
    const priceLow = basePrice * 0.95;
    const priceHigh = basePrice * 1.10;
    const buyPrice = basePrice * 0.985;

    // Build a simple 30d expected path
    const days = Array.from({ length: 30 }, (_, i) => {
        const drift = 1 + (i / 200) * vol;
        const noise = (Math.sin((i + seed) / 4) + Math.cos((i + seed) / 7)) * 0.5;
        return {
            day: `D${i + 1}`,
            price: Number((buyPrice * drift + noise).toFixed(2)),
        };
    });

    const now = new Date();
    const start = new Date(now.getTime() + 2 * 24 * 3600 * 1000);
    const end = new Date(now.getTime() + 7 * 24 * 3600 * 1000);

    const nextMonthProd = rand(1200, 4800, 3); // MWh
    const roi = rand(-2.5, 7.5, 4); // %

    return {
        companyName,
        currency: "USD",
        buyPrice: Number(buyPrice.toFixed(2)),
        priceLow: Number(priceLow.toFixed(2)),
        priceHigh: Number(priceHigh.toFixed(2)),
        confidence: Number(rand(0.65, 0.92, 5).toFixed(2)),
        riskScore: Math.round(rand(2, 8, 6)),
        riskNote: "model uncertainty & market liquidity",
        expectedRoiPct: Number(roi.toFixed(2)),
        nextMonthProductionMWh: Math.round(nextMonthProd),
        buyWindowStart: start.toISOString(),
        buyWindowEnd: end.toISOString(),
        topDrivers: [
            "historical capacity factor",
            "seasonal irradiance forecast",
            mode === "production" ? "maintenance schedule" : "demand elasticity",
        ],
        pricePath: days,
        notes:
            "Estimates incorporate seasonal patterns, recent utilization, typical outage rates, and a baseline price elasticity prior.",
    };
}

export default AIPricingAndForecast;
